import { Collection, List, Record } from 'immutable';
import { Language, ENGLISH } from 'app/constants/enums/Language';

interface ICanonicalListItem {
  id: string;
  language: Language;
  text: string;
}

// This record type is used in FilteredList and FilteredSelector
// components when we need to bring an entity to a format similar to
// that of Text, like Lesson which needs a lookup to get the title
// text.
export class CanonicalListItem extends Record({
  id: '',
  language: ENGLISH,
  text: '',
}, 'CanonicalListItem') implements ICanonicalListItem {}

interface IFuzzyFilterParams<T> {
  getItemText: (o: T) => string;
  filter: string;
  list: List<T>;
}

export function fuzzyFilterList<T>({
  getItemText,
  filter,
  list,
}: IFuzzyFilterParams<T>): List<T> {
  const lowerCaseFilter = filter.toLowerCase();

  return (
    list
    .filter((item) => isFuzzyMatch(lowerCaseFilter, getItemText(item)))
    .sort((itemA, itemB) =>
      closerFuzzyMatch(lowerCaseFilter, getItemText(itemA), getItemText(itemB)))
  );
}

export function getFirstIndexOfClosestFuzzyMatch<T>({
  getItemText,
  filter,
  list,
}: IFuzzyFilterParams<T>): number {
  const lowerCaseFilter = filter.toLowerCase();
  let matchIndex = -1;
  let matchText: string;

  list.forEach((item, index) => {
    const text = getItemText(item);

    if (isFuzzyMatch(lowerCaseFilter, text)) {
      if (
        matchIndex === -1 ||
        closerFuzzyMatch(lowerCaseFilter, matchText, text) > 0
      ) {
        matchIndex = index;
        matchText = text;
      }
    }
  });

  return matchIndex;
}

function isFuzzyMatch(filter: string, text?: string): boolean {
  return !text || fuzzyIndexOf(filter, text.toLowerCase()) >= 0;
}

export function textFilter(rawFilter: string = '') {
  const filter = rawFilter.toLowerCase();
  return ({ text }: { text?: string } = {}) => isFuzzyMatch(filter, text);
}

function closerFuzzyMatch(filter: string, a: string = '', b: string = '') {
  const indicesA = fuzzyFindChars(filter, a.toLowerCase());
  const indicesB = fuzzyFindChars(filter, b.toLowerCase());

  const lastIndex = filter.length - 1;
  const lastA = indicesA[lastIndex];
  const lastB = indicesB[lastIndex];

  return (lastA >= 0 && lastB < 0 ? -1 : // only found in A
    lastB >= 0 && lastA < 0 ? 1 :  // only found in B
    lastA - lastB);
}

export function byClosestFilterMatch(rawFilter: string) {
  if (!rawFilter) {
    // No sorting for empty filter
    return () => 0;
  }

  const filter = rawFilter.toLowerCase();

  return ({ text: a }: CanonicalListItem, { text: b }: CanonicalListItem) => closerFuzzyMatch(filter, a, b);
}

export function filterByLanguage(items: Collection<any, ICanonicalListItem>, language: Language) {
  return items.toList()
       .filter(({ id, language: itemLanguage }) => language === itemLanguage)
       .sortBy(({ text = '' }) => text.toLowerCase());
}

export function fuzzyFindChars(filter: string, text: string): Int32Array {
  const foundIndices = new Int32Array(filter.length).fill(-1);

  for (
    let
      filterCursor = 0,
      textCursor = 0;
    filterCursor < filter.length;
    ++filterCursor
  ) {
    const filterCharacter = filter[filterCursor];

    textCursor = text.indexOf(filterCharacter, textCursor);

    if (textCursor < 0) {
      break;
    }

    foundIndices[filterCursor] = textCursor;
  }

  return foundIndices;
}

export function fuzzyIndexOf(filter: string, text: string): number {
  if (filter === '') {
    return 0;
  } else if (filter.length > text.length) {
    return -1;
  } else {
    const indices = fuzzyFindChars(filter, text);

    if (indices[indices.length - 1] < 0) {
      // If not full query was found
      return -1;
    } else {
      // Otherwise return index of first query
      return indices[0];
    }
  }
}

export function filterTags(allTags: List<string>, selectedTags: List<string>, rawFilter: string = ''): List<string> {
  const filterText = rawFilter.trim().toLowerCase();

  const tagNotSelected = (tag: string) => selectedTags.indexOf(tag) === -1;
  const tagMatchesFilter = (tag: string) => fuzzyIndexOf(filterText, tag.toLowerCase()) >= 0;

  const getFilterIndices = (tag: string) => fuzzyFindChars(filterText, tag.toLowerCase());

  if (filterText) {
    // Tags where the filter text appears at the beginning of the tag come first
    return allTags
      .filter((tag) => tagNotSelected(tag) && tagMatchesFilter(tag))
      .sortBy(getFilterIndices, compareIndices);
  } else {
    return allTags.filter(tagNotSelected);
  }
}

function compareIndices(indicesA: Int32Array, indicesB: Int32Array): number {
  const firstA = indicesA[0];
  const firstB = indicesB[0];

  const lastIndex = Math.min(indicesA.length, indicesB.length) - 1;
  const lastA = indicesA[lastIndex];
  const lastB = indicesB[lastIndex];

  // When found at index 0 in both, compare which ends first
  return (firstA === 0 && firstB === 0 ? lastA - lastB :
          firstA === 0 ? -1 : // only found in A, push A up
          firstB === 0 ? 1 :  // only found in B, push B up
          0); // preserve order according to lessonCount otherwise
}
